import type { CodeCouleur } from "./types";

/**
 * Source UNIQUE des couleurs corrosion par code couleur.
 *
 * ⚠️ Les valeurs DIFFÈRENT par média, et c'est VOLONTAIRE :
 *   - `planHex`   : palette écran/plan (Tailwind, cohérent avec les tokens globals.css)
 *   - `pdfText` / `pdfBg` : couleurs PDF alignées sur le format Word de Renaud
 *   - `excelArgb` : couleurs Excel standard
 *
 * NE PAS unifier ces valeurs sans validation de Renaud (cf. code-review C6/I7) :
 * unifier casserait la correspondance du PDF avec son document Word.
 * Centraliser ici sert juste à avoir UN seul endroit à éditer.
 *
 * Exception validée par Renaud : `orange.pdfBg` a été densifié (#FFFBEB →
 * #FDEBC8) car l'ancien fond quasi blanc était invisible à l'impression. Les
 * autres valeurs restent identiques au format Word d'origine.
 */
export interface CorrosionMediaColors {
  planHex: string;
  pdfText: string;
  pdfBg: string;
  excelArgb: string;
}

export const CORROSION_PALETTE: Record<CodeCouleur, CorrosionMediaColors> = {
  green: { planHex: "#22c55e", pdfText: "#16A34A", pdfBg: "#F0FDF4", excelArgb: "FF92D050" },
  orange: { planHex: "#f97316", pdfText: "#F59E0B", pdfBg: "#FDEBC8", excelArgb: "FFFFC000" },
  red: { planHex: "#ef4444", pdfText: "#DC2626", pdfBg: "#FEE2E2", excelArgb: "FFFF0000" },
  dark: { planHex: "#991b1b", pdfText: "#7F1D1D", pdfBg: "#FCA5A5", excelArgb: "FF991B1B" },
};

/** Couleur "non mesuré" sur le plan (hors échelle corrosion). */
export const NON_MESURE_PLAN_HEX = "#5b3abf";

const CODES: CodeCouleur[] = ["green", "orange", "red", "dark"];

function deriveMap(pick: (c: CorrosionMediaColors) => string): Record<string, string> {
  return Object.fromEntries(CODES.map((code) => [code, pick(CORROSION_PALETTE[code])]));
}

/** Couleurs des pins sur le plan/écran (+ "default" pour non mesuré). */
export const PIN_PLAN_COLORS: Record<string, string> = {
  ...deriveMap((c) => c.planHex),
  default: NON_MESURE_PLAN_HEX,
};

/** Couleurs texte/pastille pour le PDF (≈ format Word de Renaud). */
export const PDF_TEXT_COLORS: Record<string, string> = deriveMap((c) => c.pdfText);

/** Couleurs de fond légères pour les lignes du tableau PDF. */
export const PDF_BG_COLORS: Record<string, string> = deriveMap((c) => c.pdfBg);

/** Remplissages ARGB (sans #) pour la colonne % Corrosion dans l'Excel. */
export const EXCEL_CORROSION_FILLS: Record<string, string> = deriveMap((c) => c.excelArgb);
